import { useELendPositions } from '@/domain/euler/positions/useELendPositions'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { useEffect, useMemo } from 'react'
import { useAccount } from 'wagmi'
import { EULER_CARD_SUB_ACCOUNT_ID, PROTOCOL_CHAIN_IDS } from '../constants'

interface UseCardEulerPositionsParams {
  /** Sub-account ID to query positions for. Defaults to EULER_CARD_SUB_ACCOUNT_ID for legacy cards. */
  subAccountId?: number
}

/**
 * Hook to fetch Euler eLend positions for card usage
 * Reuses the existing domain hook with indexer-first, onchain-fallback pattern
 *
 * Each card may have its own subAccountId (stored in solverConfig).
 * Legacy cards without a subAccountId default to EULER_CARD_SUB_ACCOUNT_ID.
 */
export function useCardEulerPositions({ subAccountId = EULER_CARD_SUB_ACCOUNT_ID }: UseCardEulerPositionsParams = {}) {
  const { address } = useAccount()
  const addSubaccount = useSubaccountStore((state) => state.addSubaccount)

  // Ensure the card's sub-account is in the active list
  // This is necessary because the refactored hooks only query fallback range (0-15)
  // plus discovered subaccounts, and card sub-accounts may be outside that range
  useEffect(() => {
    if (address) {
      addSubaccount(subAccountId)
    }
  }, [address, addSubaccount, subAccountId])

  // Reuse the existing domain hook - it already has indexer + onchain fallback
  const result = useELendPositions({
    userAddress: address,
    chainId: PROTOCOL_CHAIN_IDS.EULER,
    enabled: !!address,
  })

  // Filter to only show positions from the card's sub-account
  const cardPositions = useMemo(() => {
    return result.positions.filter((p) => p.subAccountId === subAccountId)
  }, [result.positions, subAccountId])

  return {
    ...result,
    positions: cardPositions,
  }
}
